import { css } from '@lastbrand/web-ui-styled-system/css'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'
import type { CSSProperties } from 'react'

/**
 * Maps Contentful `commonClassNames` / `mobileClassNames` strings to Panda
 * `SystemStyleObject`s. Class-name vocabulary mirrors `quince-styles/scss/common.scss`.
 *
 * Token strategy:
 *   - Colors → Web UI DS color tokens for all Contentful color keys.
 *   - Typography → DS `textStyle` tokens for named styles and font-size--N classes.
 *     Only `font-size--6` keeps legacy rem literals (no DS token below 10px).
 *   - Spacing → DS `space-*` tokens in `COMMON_SPACING_CLASS_VALUES`; dynamic numeric
 *     values (margin-top--20, max-width--320, …) go through `dynamicValueClassesToInlineStyle`.
 *   - Borders → exact legacy alpha values; DS only exposes 30%+ alpha for darkgray.
 *
 * Extraction model:
 *   Static class lookups go through {@link classnameArraysToDSStyle} and end up
 *   in `css(...)` at the call site. Panda's AST extractor cannot see those runtime
 *   compositions, so every finite value reachable from the lookup tables below
 *   is also enumerated in `panda.config.ts > staticCss` to guarantee the
 *   matching atomic CSS exists in the bundle.
 *
 *   Contentful dynamic numeric values (`max-width--320`, `margin-bottom--12`, …)
 *   are unbounded and go through {@link dynamicValueClassesToInlineStyle}, which
 *   returns a `{ className, style }` pair. The style sets scoped CSS custom
 *   properties inline (section scope: `--rt-ds-s-*`, item scope: `--rt-ds-i-*`);
 *   the className points at per-property item rules (or {@link SECTION_DYNAMIC_VARS_CSS_CLASS})
 *   that consume those properties — only for vars that are actually set.
 */

// `cantalope` (single 'u') is the Contentful spelling — do not "fix" it.
const CONTENTFUL_COLOR_TO_DS_VALUE: Record<string, string> = {
  black: 'black',
  cantalope: 'cantaloupe-100',
  white: 'white',
  offWhite: 'offwhite',
  petal: 'petal',
  paleBlue: 'blue',
  navy: 'navy',
  umber: 'umber',
  warmGray: 'warmgrey',
  gray: 'darkgray',
  green: 'green-100',
  midGray: 'silvergray',
  lightGray: 'lightgray',
  pantone: 'blue',
  darkGray2: 'mediumgray',
  newSiteRed: 'red-100',
  red: 'red-100',
  messageBackground: 'paperwhite',
  gray85: 'lightgray',
  stone: 'stone',
  lightestGray: 'paperwhite',
  offWhiteHover: 'paperwhite',
}

const BORDER_CLASSES: Record<string, SystemStyleObject> = {
  'border-black': { borderWidth: '0.0625rem', borderStyle: 'solid', borderColor: 'darkgray-trans-20' },
  'border--horizontal-black': {
    borderTopWidth: '0.0625rem',
    borderTopStyle: 'solid',
    borderTopColor: 'darkgray-trans-20',
    borderBottomWidth: '0.0625rem',
    borderBottomStyle: 'solid',
    borderBottomColor: 'darkgray-trans-20',
  },
  'border--top--grey': { borderTopWidth: '0.0625rem', borderTopStyle: 'solid', borderTopColor: 'rgb(99 99 99 / 20%)' },
  'border--top--none': { borderTop: 'none' },
  'border--bottom--grey': {
    borderBottomWidth: '0.0625rem',
    borderBottomStyle: 'solid',
    borderBottomColor: 'rgb(99 99 99 / 20%)',
  },
  'border--none': { border: 'none' },
}

// Retained for backward compatibility with callers outside this module.
// classnameArraysToDSStyle no longer uses this; it emits { base, lg } responsive values instead.
export const LEGACY_MOBILE_MEDIA_QUERY = '_mobileLegacy'

const STATIC_CLASS_STYLES: Record<string, SystemStyleObject> = {
  'd-flex': { display: 'flex' },
  'd-inline-block': { display: 'inline-block' },
  'd-inline': { display: 'inline' },
  'd-none': { display: 'none' },
  'd-contents': { display: 'contents' },
  'q-section': {
    padding: { base: '5rem 0 5.625rem', lg: '7rem 0 7.5rem' },
  },
  'flip-wrapper': { display: 'flex', justifyContent: 'center', flexWrap: 'wrap' },
  'preserve-paragraph-formatting': { whiteSpace: 'pre-wrap' },
  // Approximation of `+ .enable-paragraph-space p:empty { height: $line-height }`.
  // Legacy adjacent-sibling rule pulls line-height from the previous text-style block;
  // unreachable via Panda. `1em` falls back to the wrapper's inherited line-height.
  'enable-paragraph-space': { '& p:empty': { minHeight: '1em' } },
}

const COMMON_SPACING_CLASS_VALUES: Record<string, string> = {
  auto: 'auto',
  '1': 'space-8',
  '2': 'space-16',
  '3': 'space-24',
  '4': 'space-32',
  '5': 'space-40',
  '6': 'space-48',
  '7': 'space-56',
  '8': 'space-64',
  '10': 'space-10',
  '22': 'space-24',
  '30': 'space-32',
}

/**
 * Layout properties go through CSS custom properties (consumed by the item/section CSS
 * class). Typography properties (font-size, line-height, letter-spacing) are applied as
 * direct inline CSS values for item scope — this prevents empty vars from resolving via
 * CSS inheritance and overriding DS textStyle tokens (e.g. `body2_grosa → body-14`).
 */
const DYNAMIC_VALUE_CLASS_TO_VAR_SUFFIX: Record<string, string> = {
  'max-width': 'mw',
  'margin-bottom': 'mb',
  'margin-top': 'mt',
  height: 'h',
  'line-height': 'lh',
  'font-size': 'fs',
  'letter-spacing': 'ls',
  'padding-top': 'pt',
  'padding-right': 'pr',
  'padding-bottom': 'pb',
  'padding-left': 'pl',
}

// eslint-disable-next-line i18next/no-literal-string -- CSS custom-property suffix, not user copy
const MOBILE_VAR_SUFFIX = '-m'

// Section-level dynamic vars use `--rt-ds-s-*`; item-level use `--rt-ds-i-*`.
// Separate namespaces prevent CSS custom property inheritance from bleeding
// section-level values (e.g. height) into per-item rich-text elements.
const dynamicVar = (suffix: string, mobile = false, scope: 's' | 'i' = 'i'): string =>
  `--rt-ds-${scope}-${suffix}${mobile ? MOBILE_VAR_SUFFIX : ''}`

// Section-level: consumes --rt-ds-s-* vars set on the section wrapper.
// Typography vars (fs/lh/ls) are safe here — sections have no DS textStyle tokens to conflict with.
const SECTION_DYNAMIC_VARS_CSS_CLASS: string = css({
  maxWidth: 'var(--rt-ds-s-mw)',
  marginBottom: 'var(--rt-ds-s-mb)',
  marginTop: 'var(--rt-ds-s-mt)',
  height: 'var(--rt-ds-s-h)',
  lineHeight: 'var(--rt-ds-s-lh)',
  fontSize: 'var(--rt-ds-s-fs)',
  letterSpacing: 'var(--rt-ds-s-ls)',
  paddingTop: 'var(--rt-ds-s-pt)',
  paddingRight: 'var(--rt-ds-s-pr)',
  paddingBottom: 'var(--rt-ds-s-pb)',
  paddingLeft: 'var(--rt-ds-s-pl)',
})

// One class per property. A bundled class that always sets `paddingBottom:
// var(--rt-ds-i-pb)` (etc.) invalidates authored pads when those vars are unset —
// Chrome then computes padding to 0 and wipes `pb_space-120!` on the same node.
const ITEM_MW_VAR_CSS_CLASS: string = css({ maxWidth: 'var(--rt-ds-i-mw)' })
const ITEM_MB_VAR_CSS_CLASS: string = css({ marginBottom: 'var(--rt-ds-i-mb)' })
const ITEM_MT_VAR_CSS_CLASS: string = css({ marginTop: 'var(--rt-ds-i-mt)' })
const ITEM_H_VAR_CSS_CLASS: string = css({ height: 'var(--rt-ds-i-h)' })
const ITEM_PT_VAR_CSS_CLASS: string = css({ paddingTop: 'var(--rt-ds-i-pt)' })
const ITEM_PR_VAR_CSS_CLASS: string = css({ paddingRight: 'var(--rt-ds-i-pr)' })
const ITEM_PB_VAR_CSS_CLASS: string = css({ paddingBottom: 'var(--rt-ds-i-pb)' })
const ITEM_PL_VAR_CSS_CLASS: string = css({ paddingLeft: 'var(--rt-ds-i-pl)' })
const ITEM_FS_VAR_CSS_CLASS: string = css({ fontSize: 'var(--rt-ds-i-fs)' })
const ITEM_LH_VAR_CSS_CLASS: string = css({ lineHeight: 'var(--rt-ds-i-lh)' })
const ITEM_LS_VAR_CSS_CLASS: string = css({ letterSpacing: 'var(--rt-ds-i-ls)' })

// Sizes ≥22px map to title-N tokens (uppercase, medium weight, letterspacing-base bundled).
// Size 20 maps to body-20 (regular weight, no uppercase) — both title-20 and body-20 exist;
// body-20 is chosen so explicit class-name overrides (text-transform, font-weight) still win.
// Sizes ≤18px map to body-N tokens (no uppercase, regular weight).
// 6px has no DS token (DS minimum is 10px) so it keeps legacy rem literals.
const LEGACY_FONT_SIZE_TO_STYLE: Record<string, SystemStyleObject> = {
  '56': { textStyle: 'title-56' }, // exact match DS[56/62]
  '52': { textStyle: 'title-56' }, // nearest: DS[56/62] Δfs-4
  '50': { textStyle: 'title-48' }, // nearest: DS[48/54] Δfs+2
  '48': { textStyle: 'title-48' }, // exact match DS[48/54]
  '46': { textStyle: 'title-48' }, // nearest: DS[48/54] Δfs-2
  '44': { textStyle: 'title-40' }, // nearest: DS[40/46] Δfs+4
  '42': { textStyle: 'title-40' }, // nearest: DS[40/46] Δfs+2
  '40': { textStyle: 'title-40' }, // exact match DS[40/46]
  '38': { textStyle: 'title-40' }, // nearest: DS[40/46] Δfs-2
  '36': { textStyle: 'title-40' }, // nearest: DS[40/46] Δfs-4
  '34': { textStyle: 'title-32' }, // nearest: DS[32/40] Δfs+2
  '32': { textStyle: 'title-32' }, // exact match DS[32/40]
  '30': { textStyle: 'title-32' }, // equidistant DS[28/34]/DS[32/40]; no line-height to match, so 32 tracks the legacy fluid rem (>30px on real viewports)
  '28': { textStyle: 'title-28' }, // exact match DS[28/34]
  '26': { textStyle: 'title-28' }, // nearest: DS[28/34] Δfs-2
  '24': { textStyle: 'title-24' }, // nearest: DS[24/28] Δfs0
  '22': { textStyle: 'title-24' }, // nearest: DS[24/28] Δfs-2
  '20': { textStyle: 'body-20' }, // exact match DS[20/26]
  '18': { textStyle: 'body-18' }, // exact match DS[18/24]
  '16': { textStyle: 'body-16' }, // exact match DS[16/22]
  '15': { textStyle: 'body-16' }, // nearest: DS[16/22] Δfs-1
  '14': { textStyle: 'body-14' }, // exact match DS[14/20]
  '13': { textStyle: 'body-14' }, // nearest: DS[14/20] Δfs-1
  '12': { textStyle: 'body-12' }, // exact match DS[12/16]
  '11': { textStyle: 'body-12' }, // nearest: DS[12/16] Δfs-1
  '10': { textStyle: 'body-10' }, // nearest: DS[10/12] Δfs0
  '9': { textStyle: 'body-10' }, // nearest: DS[10/12] Δfs-1
  '6': { fontSize: '0.375rem', lineHeight: '0.75rem' }, // no DS token below 10px
}

const LETTER_SPACING_MAP: Record<string, string> = {
  '4': 'letterspacing-base',
  '5': 'letterspacing-base',
  '6': 'letterspacing-base',
  normal: 'normal',
}

const TEXT_TRANSFORM_VALUES = ['uppercase', 'lowercase', 'none'] as const
const LEGACY_FONT_WEIGHT_VALUES = new Set([100, 200, 300, 400, 500, 600, 700, 800, 900])

const LEGACY_GROSA_FONT_FAMILY = 'grosa, sans-serif'
const IVY_FONT_FAMILY_PREFIX = 'ivy-'
const LEGACY_IVY_HEADLINE_FONT_FAMILY = 'ivy-headline, serif'
const LEGACY_IVY_TEXT_FONT_FAMILY = 'ivy-text, serif'

const LEGACY_FONT_FAMILY: Record<string, string> = {
  grosa: LEGACY_GROSA_FONT_FAMILY,
  'ivy-headline': LEGACY_IVY_HEADLINE_FONT_FAMILY,
  'ivy-text': LEGACY_IVY_TEXT_FONT_FAMILY,
}

type ResponsiveStyleObject = SystemStyleObject & Record<string, unknown>

/**
 * Named legacy typography classes (`quince-styles/scss/common.scss`) → DS `textStyle`
 * tokens. Exported so the heroBanner DS shim (`hero-banner-ds-styles.ts`) resolves the
 * same vocabulary from one table instead of keeping a second, partial copy.
 */
export const NAMED_TEXT_STYLES: Record<string, SystemStyleObject> = {
  h1_ivy: { textStyle: { base: 'title-32-dec', lg: 'title-56-dec' } },
  h2_ivy: { textStyle: { base: 'title-24-dec', lg: 'title-40-dec' } },
  h3_ivy: { textStyle: 'title-32-dec' },
  // Legacy mobile is 30/36: title-28 (28/34) is within ±2 on both axes, title-32 (32/40) is not.
  // A bare `font-size--30` pins no line-height, so it is free to take the larger token below.
  h1_grosa: { textStyle: { base: 'title-28', lg: 'title-48' } },
  h2_grosa: { textStyle: { base: 'title-24', lg: 'title-32' } },
  h3_grosa: { textStyle: { base: 'title-14', lg: 'title-24' } },
  body1_ivy: { textStyle: { base: 'title-16-dec', lg: 'title-24-dec' } },
  body2_ivy: { textStyle: 'title-16-dec' },
  subtitle1_grosa: { textStyle: { base: 'title-12', lg: 'title-18' } },
  subtitle2_grosa: { textStyle: { base: 'title-10', lg: 'title-18' } },
  body1_grosa: { textStyle: 'body-16' },
  body2_grosa: { textStyle: 'body-14' },
  bodySmall_grosa: { textStyle: 'body-12' },
  cta: { textStyle: 'body-12-strong' },
}

function resolveColor(name: string): string {
  return CONTENTFUL_COLOR_TO_DS_VALUE[name] ?? name
}

function commonSpacingValue(value: string): string | undefined {
  return COMMON_SPACING_CLASS_VALUES[value]
}

function singleSpacingStyle(prefix: string, value: string): SystemStyleObject {
  const spacing = commonSpacingValue(value)
  if (!spacing) return {}

  switch (prefix) {
    case 'padding':
      return { padding: spacing }
    case 'padding-v':
      return { paddingTop: spacing, paddingBottom: spacing }
    case 'padding-h':
      return { paddingLeft: spacing, paddingRight: spacing }
    case 'padding-right':
      return { paddingRight: spacing }
    case 'margin':
      return { margin: spacing }
    case 'margin-top':
      return { marginTop: spacing }
    case 'margin-right':
      return { marginRight: spacing }
    case 'margin-bottom':
      return { marginBottom: spacing }
    case 'margin-left':
      return { marginLeft: spacing }
    case 'column-gap':
      return { display: 'flex', columnGap: spacing }
    default:
      return {}
  }
}

// DS uses 1rem = 16px. Convert all incoming Contentful px values to rem for consistency
// with the rest of the DS codebase and the legacy widget which also emits rem.
function pxToRem(px: number): string {
  return `${px / 16}rem`
}

function singleClassnameToDSStyle(cls: string): SystemStyleObject {
  if (cls in STATIC_CLASS_STYLES) return STATIC_CLASS_STYLES[cls] as SystemStyleObject
  if (cls in NAMED_TEXT_STYLES) return NAMED_TEXT_STYLES[cls] as SystemStyleObject
  if (cls in BORDER_CLASSES) return BORDER_CLASSES[cls] as SystemStyleObject

  const dashIdx = cls.indexOf('--')
  if (dashIdx === -1) return {}

  const prefix = cls.slice(0, dashIdx)
  const value = cls.slice(dashIdx + 2)

  switch (prefix) {
    case 'color':
      return { color: resolveColor(value) }

    case 'background-color':
    case 'b-colour':
      return { backgroundColor: resolveColor(value) }

    case 'background':
      return value === 'cover' ? { backgroundSize: 'cover' } : {}

    case 'font-family': {
      const ff = LEGACY_FONT_FAMILY[value]
      return ff ? { fontFamily: ff } : {}
    }

    case 'font-weight': {
      if (value === 'normal') return { fontWeight: 'normal' }
      const n = parseInt(value, 10)
      return LEGACY_FONT_WEIGHT_VALUES.has(n) && n.toString() === value ? { fontWeight: n } : {}
    }

    case 'font-size':
      return LEGACY_FONT_SIZE_TO_STYLE[value] ?? {}

    case 'font-case':
      return value === 'upper' ? { textTransform: 'uppercase' } : {}

    case 'text-transform':
      return TEXT_TRANSFORM_VALUES.includes(value as (typeof TEXT_TRANSFORM_VALUES)[number])
        ? { textTransform: value as SystemStyleObject['textTransform'] }
        : {}

    case 'text-align':
      return ['center', 'left', 'right'].includes(value) ? { textAlign: value as SystemStyleObject['textAlign'] } : {}

    case 'text-decor':
      return value === 'none' || value === 'underline'
        ? { textDecoration: value as SystemStyleObject['textDecoration'] }
        : {}

    case 'letter-spacing': {
      const ls = LETTER_SPACING_MAP[value]
      return ls ? { letterSpacing: ls } : {}
    }

    case 'line-height':
      return value === 'normal' ? { lineHeight: 'normal' } : {}

    case 'ul-list-style':
      // eslint-disable-next-line i18next/no-literal-string
      return value === 'disk' ? ({ '& ul': { listStyle: 'disc', marginLeft: 'space-32' } } as SystemStyleObject) : {}

    case 'flex':
      return value === 'wrap' ? { flexWrap: 'wrap' } : value === 'nowrap' ? { flexWrap: 'nowrap' } : {}

    case 'flex-d':
      return value === 'row' ? { flexDirection: 'row' } : value === 'column' ? { flexDirection: 'column' } : {}

    case 'align-items':
      return value === 'center' ? { alignItems: 'center' } : {}

    case 'justify-content':
      return value === 'center'
        ? { justifyContent: 'center' }
        : value === 'space-between'
          ? { justifyContent: 'space-between' }
          : {}

    // Static common.scss spacing includes padding, padding-v, padding-h, and padding-right only.
    case 'padding':
    case 'padding-v':
    case 'padding-h':
    case 'padding-right':
    case 'margin':
    case 'margin-top':
    case 'margin-right':
    case 'margin-bottom':
    case 'margin-left':
    case 'column-gap':
      return singleSpacingStyle(prefix, value)

    case 'cursor':
      return value === 'pointer' ? { cursor: 'pointer' } : {}

    case 'position':
      return ['absolute', 'relative', 'fixed'].includes(value)
        ? { position: value as SystemStyleObject['position'] }
        : {}

    case 'width':
      return value === 'auto' ? { width: 'auto' } : value === 'full' ? { width: '100%' } : {}

    case 'height':
      return value === 'auto'
        ? { height: 'auto' }
        : value === 'full'
          ? { height: '100%' }
          : value === 'fit'
            ? { height: 'fit-content' }
            : {}

    case 'float':
      return value === 'left' || value === 'right' ? { float: value as SystemStyleObject['float'] } : {}

    default:
      return {}
  }
}

function isResponsiveValue(value: unknown): value is { base?: unknown; lg?: unknown } {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && ('base' in value || 'lg' in value)
}

function mobileValue(value: unknown): unknown {
  return isResponsiveValue(value) && value.base !== undefined ? value.base : value
}

function desktopValue(value: unknown): unknown {
  return isResponsiveValue(value) && value.lg !== undefined ? value.lg : value
}

/**
 * Merges style objects last-wins.
 */
export function mergeLegacyResponsiveStyles(...styles: SystemStyleObject[]): SystemStyleObject {
  return Object.assign({}, ...styles) as SystemStyleObject
}

// The `-dec` textStyle compositions bundle `fontfamily-decorative` (ivy-headline serif
// stack) and `fontweight-light` (300) — the values these tokens are standardising on.
// The DS weight scale is fontweight-light 300 / regular 400 / medium 500. CMS weights
// already on the scale are used as-is; off-scale weights snap to the nearest DS value.
const DEC_TOKEN_FONT_WEIGHT = 300
const DS_FONT_WEIGHTS = [300, 400, 500]

function nearestDSFontWeight(weight: number): number {
  return DS_FONT_WEIGHTS.reduce((best, candidate) =>
    Math.abs(candidate - weight) < Math.abs(best - weight) ? candidate : best,
  )
}

const isDecToken = (value: unknown): boolean => typeof value === 'string' && value.endsWith('-dec')

/**
 * `font-size--N` classes map to `title-N` textStyle tokens (grosa, uppercase). When the
 * Contentful classes pair them with an ivy font family (e.g. a homepage heading:
 * `font-family--ivy-headline` + `font-size--40`), the legacy renderer shows serif text
 * WITHOUT uppercase — the `-dec` (decorative) variant of the same token. Apply this to
 * the result of {@link classnameArraysToDSStyle} for any rich text that can carry an
 * ivy font family. (Also defined locally in `tabs-section-ds-widget` on the in-flight
 * tabs-section branch — fold that copy into this export when both have merged.)
 *
 * When every resolved textStyle value is a `-dec` token, redundant raw `fontFamily`
 * (ivy-headline) and `fontWeight` overrides from the class-name mapping are normalised
 * onto the DS weight scale (light 300 / regular 400 / medium 500): a CMS weight that
 * snaps to 300 drops entirely (the token composition supplies fontweight-light), 400
 * and 500 are used as-is, and off-scale values (200, 600, 700, …) snap to the nearest
 * DS weight. An `ivy-text` family is a deliberate divergence from the token and stays
 * as an override.
 */
export function ivyDecorativeTextStyle(style: SystemStyleObject): SystemStyleObject {
  const record = style as Record<string, unknown>
  const fontFamily = record.fontFamily
  if (typeof fontFamily !== 'string' || !fontFamily.startsWith(IVY_FONT_FAMILY_PREFIX)) return style

  const toDec = (value: unknown): unknown =>
    typeof value === 'string' && value.startsWith('title-') && !value.endsWith('-dec') ? `${value}-dec` : value

  const textStyle = record.textStyle
  let result: Record<string, unknown> | undefined
  if (typeof textStyle === 'string') {
    result = { ...record, textStyle: toDec(textStyle) }
  } else if (typeof textStyle === 'object' && textStyle !== null) {
    result = {
      ...record,
      textStyle: Object.fromEntries(
        Object.entries(textStyle as Record<string, unknown>).map(([breakpoint, value]) => [breakpoint, toDec(value)]),
      ),
    }
  }
  if (!result) return style

  const resolved = result.textStyle
  const allDec =
    typeof resolved === 'string'
      ? isDecToken(resolved)
      : Object.values(resolved as Record<string, unknown>).every(isDecToken)
  if (allDec) {
    if (result.fontFamily === LEGACY_IVY_HEADLINE_FONT_FAMILY) delete result.fontFamily
    if (typeof result.fontWeight === 'number') {
      const dsWeight = nearestDSFontWeight(result.fontWeight)
      if (dsWeight === DEC_TOKEN_FONT_WEIGHT) delete result.fontWeight
      else result.fontWeight = dsWeight
    }
  }
  return result as SystemStyleObject
}

function textStyleTokenSize(token: unknown): number | undefined {
  if (typeof token !== 'string') return undefined
  const base = token.endsWith('-dec') ? token.slice(0, -4) : token
  const match = /^(?:title|body)-(\d+)(?:-strong)?$/.exec(base)
  return match ? Number(match[1]) : undefined
}

// Keep named responsive textStyle only when a later font-size--N exactly restates the desktop px.
function mergeTextStyle(existing: unknown, incoming: unknown, cls: string): unknown {
  if (!cls.startsWith('font-size--')) return incoming

  const existingIsResponsive = typeof existing === 'object' && existing !== null && 'base' in existing
  if (!existingIsResponsive || typeof incoming !== 'string') return incoming

  const responsive = existing as { base?: unknown; lg?: unknown }
  const desktopSize = textStyleTokenSize(responsive.lg ?? responsive.base)
  const fontSizeN = Number(cls.slice('font-size--'.length))
  if (!Number.isFinite(fontSizeN) || desktopSize === undefined) return incoming

  return fontSizeN === desktopSize ? existing : incoming
}

function mergeClassnames(classNames: string[]): ResponsiveStyleObject {
  return classNames.reduce<ResponsiveStyleObject>((acc, cls) => {
    const next = singleClassnameToDSStyle(cls) as ResponsiveStyleObject
    if (next.textStyle !== undefined && acc.textStyle !== undefined) {
      const textStyle = mergeTextStyle(acc.textStyle, next.textStyle, cls)
      const { textStyle: _ignored, ...restNext } = next
      return Object.assign(acc, restNext, { textStyle }) as ResponsiveStyleObject
    }
    return Object.assign(acc, next) as ResponsiveStyleObject
  }, {})
}

const declaresUppercase = (token: unknown, promotesDec: boolean): boolean =>
  !promotesDec && typeof token === 'string' && /^title-\d+$/.test(token)

/** The single `fontFamily` {@link ivyDecorativeTextStyle} sees after the merge, if it isn't responsive. */
function mergedFontFamily(baseStyles: ResponsiveStyleObject, mobileStyles: ResponsiveStyleObject): unknown {
  const mobFam = mobileValue(mobileStyles.fontFamily)
  const deskFam = desktopValue(baseStyles.fontFamily)
  if (mobFam === undefined) return deskFam
  return JSON.stringify(mobFam) === JSON.stringify(deskFam) ? deskFam : undefined
}

// `title-N` bundles uppercase + letterspacing; without a reset it leaks onto the `body-N` breakpoint.
// An ivy family becomes `-dec` after the merge, where the reset would force uppercase instead.
function mixedTextStyleResets(
  mobileToken: unknown,
  desktopToken: unknown,
  promotesDec: boolean,
): ResponsiveStyleObject {
  const mobileIsTitle = declaresUppercase(mobileToken, promotesDec)
  const desktopIsTitle = declaresUppercase(desktopToken, promotesDec)
  if (mobileIsTitle === desktopIsTitle) return {}

  /* eslint-disable i18next/no-literal-string -- CSS keywords and DS tokens, not user copy */
  return {
    textTransform: { base: mobileIsTitle ? 'uppercase' : 'none', lg: desktopIsTitle ? 'uppercase' : 'none' },
    letterSpacing: {
      base: mobileIsTitle ? 'letterspacing-base' : 'normal',
      lg: desktopIsTitle ? 'letterspacing-base' : 'normal',
    },
  }
  /* eslint-enable i18next/no-literal-string */
}

function mergeDesktopMobileStyles(
  baseStyles: ResponsiveStyleObject,
  mobileStyles: ResponsiveStyleObject,
  hasMobileStyles: boolean,
): SystemStyleObject {
  if (!hasMobileStyles) return baseStyles

  const result: ResponsiveStyleObject = { ...baseStyles }
  const fontFamily = mergedFontFamily(baseStyles, mobileStyles)
  const promotesDec = typeof fontFamily === 'string' && fontFamily.startsWith(IVY_FONT_FAMILY_PREFIX)

  for (const prop of Object.keys(mobileStyles).filter(k => !k.startsWith('@') && !k.startsWith('_'))) {
    const mobVal = mobileValue(mobileStyles[prop])
    const deskVal = desktopValue(baseStyles[prop])

    if (mobVal === undefined) continue

    if (deskVal !== undefined && JSON.stringify(deskVal) !== JSON.stringify(mobVal)) {
      result[prop] = { base: mobVal, lg: deskVal }
      if (prop === 'textStyle') {
        for (const [resetProp, value] of Object.entries(mixedTextStyleResets(mobVal, deskVal, promotesDec))) {
          // Authored text-transform / letter-spacing classes keep priority.
          if (result[resetProp] === undefined) result[resetProp] = value
        }
      }
    } else if (deskVal !== undefined) {
      result[prop] = deskVal
    } else if (prop === 'textStyle') {
      // textStyle is a composite token with no 'unset' form; a mobile-only font-size
      // class therefore applies at all widths (rare — legacy content pairs sizes).
      result.textStyle = mobVal as SystemStyleObject['textStyle']
    } else {
      // Legacy mobileOnly classes emit no desktop rule — reset at lg so mobile-only
      // values (e.g. text--center) don't leak into desktop. `display` is non-inherited
      // and `unset` resolves to `initial` (= inline), not the element's UA default —
      // `revert` rolls back to the UA value (block for a div), like a missing rule.
      // eslint-disable-next-line i18next/no-literal-string -- CSS keywords, not user copy
      result[prop] = { base: mobVal, lg: prop === 'display' ? 'revert' : 'unset' }
    }
  }

  return result as SystemStyleObject
}

/**
 * Merges `commonClassNames` (desktop) + `mobileClassNames` into a responsive
 * Panda style using the DS `lg` breakpoint (min-width: 1200px). Properties that
 * differ between common and mobile become `{ base: mobileVal, lg: desktopVal }`.
 */
export function classnameArraysToDSStyle(
  commonClassNames: string[] = [],
  mobileClassNames: string[] = [],
): SystemStyleObject {
  const baseStyles = mergeClassnames(commonClassNames)
  const mobileStyles = mergeClassnames(mobileClassNames)

  return mergeDesktopMobileStyles(baseStyles, mobileStyles, mobileClassNames.length > 0)
}

/**
 * Result of {@link dynamicValueClassesToInlineStyle}. The dynamic numeric
 * values from Contentful are unbounded, so they bypass Panda's `css(...)`
 * pipeline entirely and apply through inline `style={...}` instead.
 *
 * `style` carries scoped CSS custom properties:
 *   - `--rt-ds-s-<suffix>` (section) / `--rt-ds-i-<suffix>` (item) for desktop values
 *   - `-m` suffixed mobile layout vars (max-width, margins, height, paddings) —
 *     consumed below the DS `lg` breakpoint (width < 1200px) by
 *     `rich-text-ds-dynamic-override.module.css`
 *   - Item-scope typography (`fs`/`lh`/`ls`) uses `--rt-ds-i-*` vars + mobile `-m`
 *     overrides (same media switch as layout vars).
 *
 * `className` resolves to the atomic classes Panda generates for each present
 * item-scope var (or {@link SECTION_DYNAMIC_VARS_CSS_CLASS} for section scope).
 * Mobile overrides are handled by `rich-text-ds-dynamic-override.module.css`.
 */
export interface DynamicInlineStyle {
  style?: CSSProperties
  className?: string
}

function parsedDynamicSuffix(cls: string): { suffix: string; value: number } | undefined {
  const dashIdx = cls.indexOf('--')
  if (dashIdx === -1) return undefined

  const prefix = cls.slice(0, dashIdx)
  const rawValue = cls.slice(dashIdx + 2)
  const suffix = DYNAMIC_VALUE_CLASS_TO_VAR_SUFFIX[prefix]
  const parsedValue = parseFloat(rawValue)

  if (!suffix || !Number.isFinite(parsedValue)) return undefined

  return { suffix, value: parsedValue }
}

function dynamicVarsForClasses(classes: string[], mobile: boolean, scope: 's' | 'i'): Record<string, string> {
  const vars: Record<string, string> = {}

  for (const cls of classes) {
    const parsed = parsedDynamicSuffix(cls)
    if (!parsed) continue
    vars[dynamicVar(parsed.suffix, mobile, scope)] = pxToRem(parsed.value)
  }

  return vars
}

function itemClassesForVars(vars: Record<string, string>): string[] {
  const classes: string[] = []
  if ('--rt-ds-i-mw' in vars || '--rt-ds-i-mw-m' in vars) classes.push(ITEM_MW_VAR_CSS_CLASS)
  if ('--rt-ds-i-mb' in vars || '--rt-ds-i-mb-m' in vars) classes.push(ITEM_MB_VAR_CSS_CLASS)
  if ('--rt-ds-i-mt' in vars || '--rt-ds-i-mt-m' in vars) classes.push(ITEM_MT_VAR_CSS_CLASS)
  if ('--rt-ds-i-h' in vars || '--rt-ds-i-h-m' in vars) classes.push(ITEM_H_VAR_CSS_CLASS)
  if ('--rt-ds-i-pt' in vars || '--rt-ds-i-pt-m' in vars) classes.push(ITEM_PT_VAR_CSS_CLASS)
  if ('--rt-ds-i-pr' in vars || '--rt-ds-i-pr-m' in vars) classes.push(ITEM_PR_VAR_CSS_CLASS)
  if ('--rt-ds-i-pb' in vars || '--rt-ds-i-pb-m' in vars) classes.push(ITEM_PB_VAR_CSS_CLASS)
  if ('--rt-ds-i-pl' in vars || '--rt-ds-i-pl-m' in vars) classes.push(ITEM_PL_VAR_CSS_CLASS)
  if ('--rt-ds-i-fs' in vars || '--rt-ds-i-fs-m' in vars) classes.push(ITEM_FS_VAR_CSS_CLASS)
  if ('--rt-ds-i-lh' in vars || '--rt-ds-i-lh-m' in vars) classes.push(ITEM_LH_VAR_CSS_CLASS)
  if ('--rt-ds-i-ls' in vars || '--rt-ds-i-ls-m' in vars) classes.push(ITEM_LS_VAR_CSS_CLASS)
  return classes
}

/**
 * Maps legacy `useDynamicStyle` value classes (`margin-top--20`, `max-width--320`, …)
 * to inline-style CSS custom properties + a className.
 *
 * `scope` controls which CSS custom property namespace is used:
 * - `'section'` → `--rt-ds-s-*` vars, consumed by {@link SECTION_DYNAMIC_VARS_CSS_CLASS}
 * - `'item'` (default) → `--rt-ds-i-*` vars, consumed by per-property item classes
 *   (only properties present in `style` get a consuming class — never attach an unset var)
 *
 * Using separate namespaces prevents section-level vars (e.g. `--rt-ds-s-h`) from
 * being inherited by per-item elements that consume item-scoped vars.
 *
 * Returns an empty object when no recognised dynamic classes are present.
 */
export function dynamicValueClassesToInlineStyle(
  baseDynamicValueClasses: string[] = [],
  dynamicValueClassesMobile: string[] = [],
  scope: 'section' | 'item' = 'item',
): DynamicInlineStyle {
  const varScope: 's' | 'i' = scope === 'section' ? 's' : 'i'

  const baseVars = dynamicVarsForClasses(baseDynamicValueClasses, false, varScope)
  const mobileVars = dynamicVarsForClasses(dynamicValueClassesMobile, true, varScope)
  const mergedVars = { ...baseVars, ...mobileVars }

  if (Object.keys(mergedVars).length === 0) return {}

  const classNames = scope === 'section' ? [SECTION_DYNAMIC_VARS_CSS_CLASS] : itemClassesForVars(mergedVars)

  return {
    style: mergedVars as CSSProperties,
    className: classNames.join(' '),
  }
}
